import { useMemo, useState } from "react";
import { Box, Chip, CircularProgress, IconButton, TextField, Typography } from "@mui/material";
import { colors } from "../../../../theme/muiColors";

import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
// import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

import { DndContext, DragEndEvent, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, horizontalListSortingStrategy, sortableKeyboardCoordinates, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { KanbanPage } from "../../interfaces/kanbanPage.interface";
import { useCreateKanbanPage } from "../../hooks/useCreateKanbanPage";
import { useDeleteKanbanPage } from "../../hooks/useDeleteKanbanPage";
import { useUpdateKanbanPage } from "../../hooks/useUpdateKanbanPage";
import { useUpdateKanbanPagesOrder } from "../../hooks/useUpdateKanbanPagesOrder";
import { useDialog } from "../../../ui/dialogs/useDialog";
import { useGlobalSnackbar } from "../../../../context/SnackbarContext";
import { BenefitDisableGate } from "../../../../permissions/benefits/components/gates/BenefitDisabledGate";
import { EBenefitTypes, EShowMode } from "../../../../permissions/benefits/enums/benefits.enums";
import { Padding } from "@mui/icons-material";

interface KanbanPageSelectorProps {
  pages: KanbanPage[];
  selectedPageId: string | null;
  onSelectPage: (pageId: string) => void;
  onPageDeleted?: () => void;
  isPreviewMode?: boolean;
}

interface SortablePageProps {
  page: KanbanPage;
  selectedPageId: string | null;

  editingPageId: string | null;
  editingName: string;

  isUpdating: boolean;
  isDeleting: boolean;

  onSelectPage: (pageId: string) => void;
  onStartEdit: (page: KanbanPage) => void;
  onSaveEdit: (pageId: string) => void;
  onCancelEdit: () => void;
  onDeletePage: (page: KanbanPage) => void;

  setEditingName: (name: string) => void;
}

const Sx = {
  pageRow: {
    display: "flex",
    alignItems: "center",
    gap: 0.5,
    minWidth: 60,
  },
  editInput: { width: 120 },
  chip: (isSelected: boolean) => ({
    cursor: "pointer",
    fontWeight: isSelected ? 600 : 400,
    maxWidth: 250,
    width: "fit-content",
    flexShrink: 1,
    minWidth: 60,

    bgcolor: isSelected ? colors.orange[400] : "default",
    color: isSelected ? "white" : "default",

    "& .MuiChip-label": {
      maxWidth: 300,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      display: "block",
      minWidth: 0,
    },

    "& .MuiChip-deleteIcon": {
      margin: 0,
      marginLeft: 0.5,
      opacity: 0.7,
    },

    "&:hover": {
      bgcolor: isSelected ? colors.orange[500] : "default",
    },
  }),
  chipIconBtn: {
    p: 0.125,
    minWidth: "auto",
    color: "inherit",
    opacity: 0.7,
    transition: "0.1s ease-in-out",
    "&:hover": {
      opacity: 1,
      bgcolor: "transparent",
      transform: "translateY(-2px)",
    },
  },
  // dragHandle: (isDragging: boolean) => ({
  //   p: 0.25,
  //   cursor: isDragging ? "grabbing" : "grab",
  //   color: "inherit",
  //   opacity: 0.5,
  //   "&:hover": { opacity: 0.8 },
  // }),
};

const SortablePage = ({
  page,
  selectedPageId,
  editingPageId,
  editingName,
  isUpdating,
  isDeleting,
  onSelectPage,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onDeletePage,
  setEditingName,
}: SortablePageProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: page.uid });

  const isEditing = editingPageId === page.uid;
  const isSelected = selectedPageId === page.uid;

  // We determine if we are in preview mode implicitly if the rename/delete buttons shouldn't work.
  // Although not explicitly typed here, we get isUpdating & isDeleting as true/ignored if disabled
  const canModify = !isUpdating && !isDeleting;

  const containerStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") onSaveEdit(page.uid);
    if (e.key === "Escape") onCancelEdit();
  };

  const handleStartEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    onStartEdit(page);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDeletePage(page);
  };

  return (
    <Box ref={setNodeRef} style={containerStyle} sx={Sx.pageRow}>
      {isEditing ? (
        <>
          <TextField
            size="small"
            value={editingName}
            onChange={(e) => setEditingName(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
            disabled={isUpdating}
            sx={Sx.editInput}
          />
          <IconButton size="small" onClick={() => onSaveEdit(page.uid)} disabled={isUpdating}>
            {isUpdating ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" />}
          </IconButton>
          <IconButton size="small" onClick={onCancelEdit} disabled={isUpdating}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </>
      ) : (
        <>
          {/* <IconButton
            size="small"
            {...attributes}
            {...listeners}
            sx={Sx.dragHandle(isDragging)}
          >
            <DragIndicatorIcon sx={{ fontSize: 16 }} />
          </IconButton> */}

          <Chip
            label={page.name}
            onClick={() => onSelectPage(page.uid)}
            onDelete={(e) => e.stopPropagation()}
            deleteIcon={
              <>
                {isSelected && (
                  <Box>
                    <IconButton size="small" onClick={handleStartEdit} disabled={!canModify} sx={Sx.chipIconBtn}>
                      <EditIcon sx={{ fontSize: 16 }} />
                    </IconButton>

                    <IconButton size="small" onClick={handleDelete} disabled={!canModify} sx={Sx.chipIconBtn}>
                      <DeleteIcon sx={{ fontSize: 16, mr: 1 }} />
                    </IconButton>
                  </Box>
                )}
              </>
            }
            sx={Sx.chip(isSelected)}
          />
        </>
      )}
    </Box>
  );
};

export const KanbanPageSelector = ({ pages, selectedPageId, onSelectPage, onPageDeleted, isPreviewMode = false }: KanbanPageSelectorProps) => {
  const [editingPageId, setEditingPageId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");

  const [showNewPageInput, setShowNewPageInput] = useState(false);
  const [newPageName, setNewPageName] = useState("");

  const updatePageMutation = useUpdateKanbanPage();
  const createPageMutation = useCreateKanbanPage();
  const deletePageMutation = useDeleteKanbanPage();
  const updateOrderMutation = useUpdateKanbanPagesOrder();

  const { showDialog, DialogComponent } = useDialog();
  const { showSnackbar } = useGlobalSnackbar();

  const sortedPages = useMemo(() => [...pages].sort((a, b) => (a.order || 0) - (b.order || 0)), [pages]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const resetEditState = () => {
    setEditingPageId(null);
    setEditingName("");
  };

  const resetNewPageState = () => {
    setShowNewPageInput(false);
    setNewPageName("");
  };

  const handleStartEdit = (page: KanbanPage) => {
    setEditingPageId(page.uid);
    setEditingName(page.name);
  };

  const handleSaveEdit = async (pageId: string) => {
    const name = editingName.trim();
    if (!name) {
      resetEditState();
      return;
    }

    try {
      await updatePageMutation.mutateAsync({
        pageId,
        updates: { name },
      });
      resetEditState();
      showSnackbar("Kanban page updated successfully!", "success", 3000, "bottom-left");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to update kanban page. Please try again.";
      showSnackbar(message, "error", 6000, "bottom-left");
    }
  };

  const handleCancelEdit = () => {
    resetEditState();
  };

  const handleCreatePage = async () => {
    const name = newPageName.trim();
    if (!name) return;

    try {
      const newPage = await createPageMutation.mutateAsync(name);
      resetNewPageState();
      onSelectPage(newPage.uid);
      showSnackbar("Kanban page created successfully!", "success", 3000, "bottom-left");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to create kanban page. Please try again.";
      showSnackbar(message, "error", 6000, "bottom-left");
    }
  };

  const handleDeletePage = (page: KanbanPage) => {
    showDialog({
      type: "confirm",
      title: "Delete Kanban Page",
      message: `Are you sure you want to delete "${page.name}"? This action cannot be undone. All tasks associated with this page will be permanently deleted.`,
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      onConfirm: async () => {
        try {
          await deletePageMutation.mutateAsync(page.uid);
          showSnackbar("Kanban page deleted successfully!", "success", 5000, "bottom-left");

          if (selectedPageId === page.uid && pages.length > 1) {
            const remainingPages = pages.filter((p) => p.uid !== page.uid);
            if (remainingPages.length > 0) onSelectPage(remainingPages[0].uid);
          }

          onPageDeleted?.();
        } catch (error) {
          const message = error instanceof Error ? error.message : "Failed to delete kanban page. Please try again.";
          showSnackbar(message, "error", 6000, "bottom-left");
        }
      },
      onCancel: () => {
        // no-op
      },
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    if (isPreviewMode) return; // Disallow dragging in preview mode.

    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sortedPages.findIndex((p) => p.uid === active.id);
    const newIndex = sortedPages.findIndex((p) => p.uid === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reorderedPages = arrayMove(sortedPages, oldIndex, newIndex);
    const pageOrders = reorderedPages.map((p, index) => ({
      pageId: p.uid,
      order: index,
    }));

    updateOrderMutation.mutate(pageOrders);
  };

  const getNextPageName = () => {
    const pageNumbers = sortedPages
      .map((p) => {
        const match = p.name.match(/Page (\d+)/);
        return match ? Number.parseInt(match[1], 10) : 0;
      })
      .filter((n) => n > 0);

    const nextNumber = pageNumbers.length > 0 ? Math.max(...pageNumbers) + 1 : sortedPages.length + 1;

    return `Page ${nextNumber}`;
  };

  const handleNewPageKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleCreatePage();
    if (e.key === "Escape") resetNewPageState();
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={sortedPages.map((p) => p.uid)} strategy={horizontalListSortingStrategy}>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
          <Typography sx={{ fontSize: "0.8rem", fontWeight: 500, color: colors.grey[500], mr: 1 }}>Pages</Typography>
          {sortedPages.map((page) => (
            <SortablePage
              key={page.uid}
              page={page}
              selectedPageId={selectedPageId}
              editingPageId={editingPageId}
              editingName={editingName}
              isUpdating={isPreviewMode || (updatePageMutation.isPending && editingPageId === page.uid)}
              isDeleting={isPreviewMode || deletePageMutation.isPending}
              onSelectPage={onSelectPage}
              onStartEdit={handleStartEdit}
              onSaveEdit={handleSaveEdit}
              onCancelEdit={handleCancelEdit}
              onDeletePage={handleDeletePage}
              setEditingName={setEditingName}
            />
          ))}

          {showNewPageInput && !isPreviewMode ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <TextField
                size="small"
                placeholder={getNextPageName()}
                value={newPageName}
                onChange={(e) => setNewPageName(e.target.value)}
                onKeyDown={handleNewPageKeyDown}
                autoFocus
                sx={{ width: 120 }}
              />
              <IconButton size="small" onClick={handleCreatePage}>
                <CheckIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={resetNewPageState}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : isPreviewMode ? null : (
            // <BenefitDisableGate benefit={EBenefitTypes.FLOW_DO} blockedTooltip="Your limit has been exceeded" showWhenBlocked={EShowMode.DISABLED}>
            <IconButton size="small" onClick={() => setShowNewPageInput(true)}>
              <AddIcon />
            </IconButton>
            // </BenefitDisableGate>
          )}

          {DialogComponent}
        </Box>
      </SortableContext>
    </DndContext>
  );
};
